import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
    Box, Typography, Button, Chip, Alert, IconButton, Tooltip, useTheme
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

export default function PendingReviewSection({ refresh, onResolved }) {
    const { chatbotId } = useParams();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [facts, setFacts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPendingFacts();
    }, [chatbotId, refresh]);

    const fetchPendingFacts = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/facts/pending-review', {
                params: { chatbot_id: chatbotId }
            });
            if (response.data.success) {
                setFacts(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching pending review facts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolve = async (factId, action) => {
        try {
            await axiosInstance.post(`/api/facts/${factId}/resolve`, { action }, {
                params: { chatbot_id: chatbotId }
            });
            fetchPendingFacts();
            onResolved();
        } catch (error) {
            console.error('Error resolving fact:', error);
        }
    };

    const columns = [
        {
            name: 'Entity',
            selector: row => row.entity_name,
            sortable: true,
            width: '150px',
        },
        {
            name: 'Attribute',
            selector: row => row.attribute_name,
            sortable: true,
            width: '150px',
        },
        {
            name: 'Value',
            selector: row => row.attribute_value,
            wrap: true,
            grow: 2,
        },
        {
            name: 'Source',
            selector: row => row.source_type,
            width: '100px',
            cell: row => (
                <Chip label={row.source_type || 'manual'} size="small" variant="outlined" />
            )
        },
        {
            name: 'Actions',
            width: '150px',
            cell: row => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Keep as Active">
                        <IconButton size="small" color="success" onClick={() => handleResolve(row.id, 'keep')}>
                            <CheckCircleIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Mark as Expired">
                        <IconButton size="small" color="error" onClick={() => handleResolve(row.id, 'expire')}>
                            <CancelIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    if (facts.length === 0 && !loading) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No facts pending review.
            </Alert>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <DataTable
                columns={columns}
                data={facts}
                progressPending={loading}
                pagination
                paginationPerPage={10}
                dense
                highlightOnHover
                theme={isDark ? 'dark' : 'default'}
                customStyles={{
                    headCells: { style: { fontWeight: 600, fontSize: '0.8rem' } },
                    cells: { style: { fontSize: '0.85rem' } },
                }}
            />
        </Box>
    );
}
